/**
 * Formulário com validação zod e estado local.
 *
 * Não usamos react-hook-form: os formulários deste app têm 1 a 4 campos e a
 * regra de validação já mora num schema zod compartilhado com o servidor.
 * Uma dependência a menos, e o estado de envio fica visível para o botão
 * (`pending`) — que é o que impede o botão de "pular" de tamanho.
 *
 * -------------------------------------------------------------------------
 * TIPAGEM (zod 4)
 * -------------------------------------------------------------------------
 * No zod 3 a assinatura era `ZodType<Output, ZodTypeDef, Input>`. No zod 4 o
 * parâmetro do meio sumiu e virou `ZodType<Output, Input, Internals>`, onde
 * `Internals` é o que de fato carrega os tipos — `ZodObject` estende
 * `_ZodType<$ZodObjectInternals<Shape, Config>>`, não `ZodType<O, I>`. Escrever
 * a restrição como `z.ZodType<TOutput, TValues>` faz o compilador tentar casar
 * `$ZodObjectInternals` com `$ZodTypeInternals<TOutput, TValues>`, a inferência
 * desiste e TODO campo do formulário volta como `unknown`.
 *
 * Por isso o genérico aqui é o SCHEMA (`TSchema extends z.ZodType`), e os tipos
 * dos valores saem de `z.input` / `z.output`, que continuam valendo no zod 4 e
 * são a forma recomendada de escrever função genérica sobre schema.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import type { z } from 'zod';

import { humanizeError } from './errors';

/** O que o formulário edita: a ENTRADA do schema (antes de transformar). */
export type FormValues<TSchema extends z.ZodType> = z.input<TSchema> & Record<string, unknown>;

export type FieldErrors<TValues> = Partial<Record<keyof TValues & string, string>>;

export interface UseZodFormResult<TValues> {
  values: TValues;
  errors: FieldErrors<TValues>;
  /** Erro que não pertence a nenhum campo (credencial inválida, 500...). */
  formError: string | null;
  pending: boolean;
  setField: <K extends keyof TValues & string>(name: K, value: TValues[K]) => void;
  setFormError: (message: string | null) => void;
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
  reset: (next?: TValues) => void;
}

export interface UseZodFormOptions<TSchema extends z.ZodType> {
  schema: TSchema;
  initialValues: FormValues<TSchema>;
  onSubmit: (values: z.output<TSchema>) => Promise<void> | void;
}

export function useZodForm<TSchema extends z.ZodType>({
  schema,
  initialValues,
  onSubmit,
}: UseZodFormOptions<TSchema>): UseZodFormResult<FormValues<TSchema>> {
  type TValues = FormValues<TSchema>;

  const [values, setValues] = useState<TValues>(initialValues);
  const [errors, setErrors] = useState<FieldErrors<TValues>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);

  // `initialValues` costuma ser um literal novo a cada render; sem congelar,
  // `reset` mudaria de identidade e derrubaria a memoização de quem o usa.
  const initialRef = useRef(initialValues);

  const setField = useCallback(<K extends keyof TValues & string>(name: K, value: TValues[K]) => {
    setValues((current) => ({ ...current, [name]: value }));
    // erro do campo some ao digitar; o erro geral só some no próximo envio
    setErrors((current) => {
      if (current[name] === undefined) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
  }, []);

  const reset = useCallback((next?: TValues) => {
    setValues(next ?? initialRef.current);
    setErrors({});
    setFormError(null);
  }, []);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      // duplo clique/Enter repetido não manda dois convites
      if (inFlight.current) return;

      setFormError(null);
      const parsed = schema.safeParse(values);

      if (!parsed.success) {
        const nextErrors: FieldErrors<TValues> = {};
        for (const issue of parsed.error.issues) {
          const key = issue.path[0];
          if (typeof key !== 'string') continue;
          const field = key as keyof TValues & string;
          if (nextErrors[field] === undefined) nextErrors[field] = issue.message;
        }
        setErrors(nextErrors);
        return;
      }

      setErrors({});
      inFlight.current = true;
      setPending(true);

      void Promise.resolve()
        .then(() => onSubmit(parsed.data))
        .catch((error: unknown) => {
          setFormError(humanizeError(error));
        })
        .finally(() => {
          inFlight.current = false;
          setPending(false);
        });
    },
    [onSubmit, schema, values],
  );

  return useMemo(
    () => ({ values, errors, formError, pending, setField, setFormError, handleSubmit, reset }),
    [values, errors, formError, pending, setField, handleSubmit, reset],
  );
}
